import { useEffect, useId, type ReactNode } from 'react';

import { useLocale } from '../i18n/LocaleProvider';
import {
  canSubmitReason,
  REASON_MAX_LENGTH,
  reasonCharactersMissing,
  reasonState,
} from '../lib/audit-reason';

/**
 * The optional typed-reason field (work order G4, AC-G4c). When given, the dialog renders a
 * textarea above the buttons and holds confirm until the reason is one the API will take.
 */
export interface ConfirmDialogReason {
  readonly value: string;
  readonly onChange: (value: string) => void;
}

/**
 * The confirm step in front of an irreversible operator action.
 *
 * Extracted from the backfill and broadcast pages, which had grown the same dialog twice — same
 * markup, same Esc handler, same rule. The rule is the reason it is one component now: **overlay
 * click is deliberately NOT a dismiss path**. Both actions land in a merchant's data or a
 * merchant's admin and cannot be recalled from this UI, so a misclick on the backdrop must not
 * silently cancel the operator's intent. Esc, or one of the two buttons.
 *
 * `confirmDisabled` holds the confirm button until the body's own precondition is met — the
 * shop file's numbering tab uses it for the "none of these numbers was used anywhere" acknowledgement a
 * lowered counter needs.
 *
 * `reason` is additive and optional: the queue controls and the failed-job actions ask for the
 * typed reason their audit row stores HERE, at the confirm step, rather than in a form of their
 * own. Without the prop the dialog renders and behaves exactly as before — the backfill and
 * numbering tabs and the broadcast page carry their reason in their forms.
 */
export function ConfirmDialog({
  open,
  title,
  confirmLabel,
  onConfirm,
  onCancel,
  confirmDisabled = false,
  reason,
  children,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly confirmDisabled?: boolean;
  readonly reason?: ConfirmDialogReason;
  readonly children: ReactNode;
}) {
  const { t, tCount } = useLocale();

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  const titleId = useId();
  const descId = useId();
  const reasonId = useId();
  const reasonHintId = useId();

  if (!open) return null;

  const reasonBlocks = reason !== undefined && !canSubmitReason(reason.value);
  const state = reason ? reasonState(reason.value) : 'ok';

  return (
    // `m-auto` rather than `items-center`: a centred flex child taller than the screen (a long
    // body, or a phone's keyboard up under the reason field) is cut off at the top where no
    // scroll reaches it; an auto-margin child centres the same way but scrolls from its top.
    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex overflow-y-auto p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="m-auto bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 max-w-md w-full shadow-2xl"
      >
        <h2 id={titleId} className="text-lg font-bold text-gray-900 mb-2">
          {title}
        </h2>
        <div id={descId} className="text-gray-600 text-sm mb-5">
          {children}
        </div>
        {reason && (
          <div className="mb-5">
            <label htmlFor={reasonId} className="field-label">
              {t('reasonPrompt.label')}
            </label>
            <textarea
              id={reasonId}
              value={reason.value}
              onChange={(event) => reason.onChange(event.target.value)}
              rows={3}
              maxLength={REASON_MAX_LENGTH}
              placeholder={t('reasonPrompt.placeholder')}
              aria-describedby={reasonHintId}
              aria-invalid={state === 'short' || state === 'long'}
              // The reason is the one thing left to do here, so it takes focus, not the
              // (still disabled) confirm button.
              autoFocus
              className="field-input"
            />
            <p id={reasonHintId} className="field-hint">
              {state === 'short'
                ? tCount(
                    'reasonPrompt.missing',
                    reasonCharactersMissing(reason.value),
                  )
                : state === 'long'
                  ? t('reasonPrompt.tooLong')
                  : t('reasonPrompt.hint')}
            </p>
          </div>
        )}
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="btn-neutral btn-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirmDisabled || reasonBlocks}
            autoFocus={reason === undefined}
            className="btn-danger btn-sm"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
